import React, { Component } from 'react'
import Head from 'next/head'
import PropTypes from 'prop-types'
import { Field, reduxForm, FieldArray, formValueSelector } from 'redux-form'
import { connect } from 'react-redux'
import classNames from 'classnames'
import { Row, Col } from 'flexbox'
import { Modal, Button, InputForm, IonIcon, Spinner } from 'controls'
import { SimpleDropDownForm } from 'controls/DropDown'
import { FiscalInfoActionCreators } from 'app/reducers/FiscalInfoRedux'
import { saveFormData, getStoredFormData, clearFiscalProgress } from 'app/utils/fiscalProgressStorage'
import fiscalInfoValidator from 'app/validators/fiscalInfoValidator'
import PDFViewer from 'components/PDFViewer'
import FiscalInfoModalTour from 'components/Layout/Admin/FiscalInfoModal/Tour'
import { getNationalities } from 'app/selectors/config'
import { getInitialInfoBrokerDataSelector } from 'app/selectors/user'
import { store } from 'services'
import { optionsForSelect } from 'components/utils'
import styles, { sequentialDocuments, documentsCompletedStyles } from './styles'

const SERVICE_CONTRACT = 'service-contract'
const BENEFICIARY_DECLARATION = 'beneficiary-declaration'
const BUSINESS_STRATEGY_CONTRACT = 'business-strategy-contract'

const DOCUMENT_NAMES = {
  [SERVICE_CONTRACT]: 'Contrato de prestación de servicios',
  [BENEFICIARY_DECLARATION]: 'Declaración de beneficiarios',
  [BUSINESS_STRATEGY_CONTRACT]: 'Estrategia comercial de marketing y remuneración',
}

const PercentageIndicator = ({ beneficiaries }) => {
  const calculateTotal = () => {
    if (!beneficiaries || !Array.isArray(beneficiaries)) return 0

    return beneficiaries.reduce((total, beneficiary) => {
      if (!beneficiary) return total

      const hasName = beneficiary.nombre && beneficiary.nombre.trim()
      const hasRelationship =
        beneficiary.parentesco && beneficiary.parentesco.trim()
      const hasAddress = beneficiary.domicilio && beneficiary.domicilio.trim()
      const hasPercentage =
        beneficiary.porcentaje &&
        beneficiary.porcentaje.toString().trim() &&
        !isNaN(Number(beneficiary.porcentaje)) &&
        Number(beneficiary.porcentaje) > 0

      // Only count complete beneficiaries
      const isComplete =
        hasName && hasRelationship && hasAddress && hasPercentage

      if (isComplete) {
        const percentage = Number(beneficiary.porcentaje)
        if (percentage >= 1 && percentage <= 100) {
          return total + percentage
        }
      }

      return total
    }, 0)
  }

  const total = calculateTotal()
  const isValid = total === 100
  const hasAnyPercentage = total > 0

  if (!hasAnyPercentage) return null

  return (
    <div className="percentage-indicator">
      <span className={`percentage-total ${isValid ? 'valid' : 'invalid'}`}>
        Total de porcentajes: {total}% {isValid ? '✓' : `(requiere 100%)`}
      </span>
      <style jsx>{`
        .percentage-indicator {
          text-align: center;
          margin: 15px 0;
          padding: 10px;
          border-radius: 4px;
          background-color: #f5f7fa;
          border: 1px solid #e0e5ee;
        }

        .percentage-total {
          font-weight: 600;
          font-size: 14px;
        }

        .percentage-total.valid {
          color: #27ae60;
        }

        .percentage-total.invalid {
          color: #e8011b;
        }
      `}</style>
    </div>
  )
}

const BeneficiaryFields = ({ fields, meta: { touched, error } }) => (
  <div className="beneficiaries-container" data-tour="fiscal-beneficiarios">
    <div className="beneficiaries-header">
      <section className="beneficiaries-title">
        <h4>Beneficiarios</h4>
      </section>
      <p className="beneficiaries-subtitle">
        Debe completar al menos 1 beneficiario. La suma de porcentajes debe ser
        exactamente 100%
      </p>
      {touched && error && error._error && (
        <span className="beneficiaries-error">{error._error}</span>
      )}
    </div>

    <div className="beneficiaries-content">
      {fields.map((member, index) => (
        <div key={index} className="beneficiary-group">
          <div className="beneficiary-header">
            <h5>Beneficiario {index + 1}</h5>
            {fields.length > 1 && (
              <button
                type="button"
                className="remove-beneficiary-btn"
                onClick={() => fields.remove(index)}
              >
                <IonIcon icon="ios-close" />
              </button>
            )}
          </div>
          <Row className="fiscal-form-row">
            <Col xs={12} md={6}>
              <Field
                name={`${member}.nombre`}
                id={`beneficiary-name-${index}`}
                component={InputForm}
                placeholder="Nombre completo"
                label="Nombre completo"
                className="form-input-margin"
              />
            </Col>
            <Col xs={12} md={6}>
              <Field
                name={`${member}.parentesco`}
                id={`beneficiary-relationship-${index}`}
                component={InputForm}
                placeholder="Parentesco"
                label="Parentesco"
                className="form-input-margin"
              />
            </Col>
          </Row>
          <Row className="fiscal-form-row">
            <Col xs={12} md={8}>
              <Field
                name={`${member}.domicilio`}
                id={`beneficiary-address-${index}`}
                component={InputForm}
                placeholder="Domicilio completo"
                label="Domicilio completo"
                className="form-input-margin"
              />
            </Col>
            <Col xs={12} md={4}>
              <Field
                name={`${member}.porcentaje`}
                id={`beneficiary-percentage-${index}`}
                component={InputForm}
                type="number"
                min="1"
                max="100"
                step="1"
                placeholder="Porcentaje (1-100)"
                label="Porcentaje (%)"
                className="form-input-margin"
              />
            </Col>
          </Row>
          {touched && error && error[index] && (
            <div className="beneficiary-errors">
              {Object.keys(error[index]).map(fieldName => (
                <span key={fieldName} className="beneficiary-field-error">
                  {error[index][fieldName]}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}

      {fields.length < 3 && (
        <div className="add-beneficiary-container">
          <Button
            type="button"
            color="primary"
            outline
            onClick={() => fields.push({})}
            className="add-beneficiary-btn"
          >
            <IonIcon icon="ios-add" />
            Agregar Beneficiario
          </Button>
        </div>
      )}
    </div>
    <style jsx>{styles}</style>
  </div>
)

const GeneratingDocuments = ({ isSubmitting, children }) => {
  if (!isSubmitting) {
    return children
  }

  return (
    <div className="">
      <div className="generation-loading-content">
        <Spinner size={50} />
        <h3>Generando documentos...</h3>
        <p>
          Por favor, espere mientras procesamos su información y
          generamos los documentos necesarios.
        </p>
      </div>
      <style jsx>{styles}</style>
    </div>
  )
}


class FiscalInfoModal extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    initialize: PropTypes.func, // redux-form initialize function
    invalid: PropTypes.bool,
    loading: PropTypes.bool,
    generateInitialDocuments: PropTypes.func.isRequired,
    acceptContracts: PropTypes.func.isRequired,
    acceptDocument: PropTypes.func.isRequired,
    updateDocumentProgress: PropTypes.func.isRequired,
    getInitialInfoBroker: PropTypes.func, // API call to fetch broker data
    fiscalInfoFeedback: PropTypes.object,
    fiscalDocuments: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      url: PropTypes.string,
    })),
    acceptanceFeedback: PropTypes.object,
    acceptedDocuments: PropTypes.array,
    currentDocumentIndex: PropTypes.number,
    initialBrokerData: PropTypes.object, // broker data from selector
  }

  static defaultProps = {
    isOpen: true,
    loading: false,
    invalid: false,
  }

  constructor(props) {
    super(props)
    
    // Initialize state with persisted progress or defaults
    this.state = {
      showPDFViewer: false,
      currentPDF: null,
      currentPDFName: null,
      currentDocumentIndex: props.currentDocumentIndex || 0,
      acceptedDocuments: props.acceptedDocuments || [],
      isAcceptingDocument: false,
      currentDocumentAcceptButtonDisabled: true,
      isFullscreenFallback: false,
    }
  }

  componentDidMount() {
    // Fetch initial broker data when component mounts
    const { getInitialInfoBroker } = this.props
    if (getInitialInfoBroker) {
      getInitialInfoBroker()
    }

    // Initialize form with stored data or broker data
    const { initialBrokerData, initialize } = this.props
    const storedFormData = getStoredFormData()
    
    if (initialize) {
      if (storedFormData) {
        // If we have stored form data, use it directly
        initialize(storedFormData)
      } else {
        const hasValidBrokerData = initialBrokerData && (
          initialBrokerData.nationality || 
          initialBrokerData.rfc || 
          initialBrokerData.curp || 
          initialBrokerData.notificationAddress
        )
        
        if (hasValidBrokerData) {
          // If we have broker data but no stored data, use broker data
          const initialValues = getInitialFormValues(initialBrokerData)
          initialize(initialValues)
        }
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Sync local state with Redux state when progress changes
    if (
      prevProps.acceptedDocuments !== this.props.acceptedDocuments ||
      prevProps.currentDocumentIndex !== this.props.currentDocumentIndex
    ) {
      this.setState({
        acceptedDocuments: this.props.acceptedDocuments || [],
        currentDocumentIndex: this.props.currentDocumentIndex || 0,
      })
    }

    // Clear loading state when acceptance API finishes (success or failure)
    if (
      prevProps.acceptanceFeedback?.loading &&
      !this.props.acceptanceFeedback?.loading
    ) {
      this.setState({ isAcceptingDocument: false })
      
      // Don't clear SessionStorage here - let the user see the completion message first
      // SessionStorage will be cleared when they close the modal manually
    }

    // Clear loading state if document generation fails
    if (
      prevProps.fiscalInfoFeedback?.loading &&
      !this.props.fiscalInfoFeedback?.loading &&
      this.props.fiscalInfoFeedback?.error
    ) {
      this.setState({ isAcceptingDocument: false })
    }

    // Initialize form with broker data when it becomes available
    const hasValidBrokerData = (data) => {
      return data && (data.nationality || data.rfc || data.curp || data.notificationAddress)
    }
    
    const prevHadValidData = hasValidBrokerData(prevProps.initialBrokerData)
    const currentHasValidData = hasValidBrokerData(this.props.initialBrokerData)
    
    if (
      !prevHadValidData && 
      currentHasValidData && 
      this.props.initialize &&
      !getStoredFormData()
    ) {
      const initialValues = getInitialFormValues(this.props.initialBrokerData)
      this.props.initialize(initialValues)
    }

    // Note: Intentionally NOT auto-closing modal to allow user to read completion message
    // The user must manually close the modal using the "Continuar con el proceso" button
    // This gives them time to read the success message and understand the process is complete
  }

  handleFormSubmit = values => {
    // Save form data to SessionStorage for persistence
    saveFormData(values)
    
    // Transform form data to match API structure
    const transformedData = {
      CURP: values.curp || '',
      RFC: values.rfc,
      nationality: values.nationality,
      notification_address: values.notificationAddress || '',
      beneficiaries: (values.beneficiaries || [])
        .filter(
          beneficiary =>
            beneficiary &&
            beneficiary.nombre &&
            beneficiary.parentesco &&
            beneficiary.domicilio &&
            beneficiary.porcentaje,
        )
        .map(beneficiary => ({
          full_name: beneficiary.nombre,
          address: beneficiary.domicilio,
          percentage: beneficiary.porcentaje,
          relationship: beneficiary.parentesco,
        })),
    }

    // Call the saga action - wrap data in the format the saga expects
    this.props.generateInitialDocuments(transformedData)
  }

  handleAcceptDocument = () => {
    const { fiscalDocuments, acceptDocument, acceptContracts } = this.props
    const { currentDocumentIndex, acceptedDocuments } = this.state

    // Set loading state
    this.setState({ isAcceptingDocument: true })

    const currentDocumentKey = fiscalDocuments[currentDocumentIndex].key
    const newAcceptedDocuments = [
      ...acceptedDocuments,
      currentDocumentKey,
    ]

    // Simulate processing delay for better UX
    setTimeout(() => {
      // Check if there are more documents
      if (currentDocumentIndex + 1 < fiscalDocuments.length) {
        // Move to next document and persist progress
        const nextIndex = currentDocumentIndex + 1
        acceptDocument(currentDocumentKey, nextIndex)
        
        this.setState({
          currentDocumentIndex: nextIndex,
          acceptedDocuments: newAcceptedDocuments,
          isAcceptingDocument: false,
          currentDocumentAcceptButtonDisabled: true,
        })
      } else {
        // All documents accepted, call the acceptance endpoint
        acceptDocument(currentDocumentKey, currentDocumentIndex)
        
        this.setState({
          acceptedDocuments: newAcceptedDocuments,
        })
        // Call the acceptance API (keep loading state until API responds)
        acceptContracts()
      }
    }, 800) // Small delay to show loading state
  }

  handleViewPDF = (pdfUrl, documentName) => {
    this.setState({
      showPDFViewer: true,
      currentPDF: pdfUrl,
      currentPDFName: documentName,
    })
  }

  handleClosePDFViewer = () => {
    this.setState({
      showPDFViewer: false,
      currentPDF: null,
      currentPDFName: null,
    })
  }


  handleCloseModal = () => {
    // Clear SessionStorage when modal is closed after completion
    clearFiscalProgress()
    // Call the original onClose prop
    this.props.onClose()
  }

  renderSequentialDocuments = () => {
    const { fiscalDocuments, acceptanceFeedback, acceptedDocuments: reduxAcceptedDocuments } = this.props
    const {
      currentDocumentIndex,
      acceptedDocuments: localAcceptedDocuments,
      isAcceptingDocument,
      currentDocumentAcceptButtonDisabled,
    } = this.state

    if (!fiscalDocuments || fiscalDocuments.length === 0) {
      return null
    }

    const totalDocuments = fiscalDocuments.length
    
    // Use Redux state for accepted documents, fallback to local state
    const acceptedDocuments = reduxAcceptedDocuments && reduxAcceptedDocuments.length > 0 
      ? reduxAcceptedDocuments 
      : localAcceptedDocuments

    // Check if all documents have been accepted
    const allDocumentsAccepted =
      acceptedDocuments.length === totalDocuments
    const isProcessingAcceptance = acceptanceFeedback?.loading
    const hasAcceptanceError =
      acceptanceFeedback?.error ||
      (acceptanceFeedback?.errors &&
        Object.keys(acceptanceFeedback.errors).length > 0)

    if (allDocumentsAccepted) {
      return (
        <div className="documents-completed">
          {isProcessingAcceptance ? (
            <div className="processing-acceptance">
              <div className="processing-content">
                <Spinner size={60} />
                <h2>Procesando aceptación...</h2>
                <p>
                  Estamos registrando la aceptación de sus documentos. Por
                  favor, espere un momento.
                </p>
              </div>
            </div>
          ) : hasAcceptanceError ? (
            <div className="acceptance-error">
              <div className="error-content">
                <IonIcon icon="ios-warning" />
                <h2>Error al procesar</h2>
                <p>
                  Hubo un error al registrar la aceptación de los documentos.
                  Por favor, inténtelo nuevamente.
                </p>
              </div>
              <div className="error-actions">
                <Button
                  type="button"
                  color="danger"
                  outline
                  fullWidth
                  onClick={() => {
                    // Reset state to allow retry
                    this.setState({
                      currentDocumentIndex: fiscalDocuments.length - 1,
                      acceptedDocuments: acceptedDocuments.slice(0, -1),
                    })
                  }}
                >
                  Reintentar
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="completion-content">
                <div className="success-icon-wrapper">
                  <div className="success-icon-circle">
                    <IonIcon icon="ios-checkmark" className="success-checkmark" />
                  </div>
                </div>
                
                <h2 className="completion-main-title">
                  Documentos Aceptados 🎊
                </h2>
                
                <div className="completion-message-box">
                  <div className="message-item">
                    <span className="message-icon">✅</span>
                    <p>
                      Has completado exitosamente la revisión y aceptación de todos los documentos.
                    </p>
                  </div>
                  
                  <div className="message-item">
                    <span className="message-icon">📧</span>
                    <p>
                      Te hemos enviado un correo con los datos de acceso a tu email y dashboard de iad.
                    </p>
                  </div>
                  
                  <div className="message-item">
                    <span className="message-icon">🚀</span>
                    <p>
                      A continuación serás redirigido a <strong>Tu Perfil</strong>, donde podrás completar los datos faltantes y descubrir todas las funcionalidades que iad Inmobiliaria tiene para ti.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="completion-actions">
                <Button
                  type="button"
                  color="primary"
                  fullWidth
                  onClick={this.handleCloseModal}
                >
                  <span>Continuar con el proceso</span>
                  <IonIcon icon="ios-arrow-forward" style={{ marginLeft: '8px' }} />
                </Button>
              </div>
            </>
          )}

          <style jsx>{documentsCompletedStyles}</style>
        </div>
      )
    }

    const { url: currentPdfUrl, key: currentKey } = fiscalDocuments[currentDocumentIndex]
    const currentDocumentName = DOCUMENT_NAMES[currentKey] || currentKey
    const documentNumber = currentDocumentIndex + 1

    return (
      <div className="sequential-documents">
        <div className="current-document">
          <div className="document-viewer">
            <PDFViewer
              file={currentPdfUrl}
              documentName={currentDocumentName}
              onClose={() => {}} // Disable close for sequential flow
              onCompleteScroll={() => {
                this.setState({
                  currentDocumentAcceptButtonDisabled: false,
                })
              }}
            >
              <div className="document-progress">
                <span className="progress-counter">
                  Documento {documentNumber} de {totalDocuments}
                </span>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${(acceptedDocuments.length / totalDocuments) *
                        100}%`,
                    }}
                  />
                </div>
              </div>

              <div className="document-actions">
                <Button
                  fullWidth
                  type="button"
                  color="primary"
                  size="large"
                  disabled={isAcceptingDocument || currentDocumentAcceptButtonDisabled}
                  loading={isAcceptingDocument}
                  onClick={this.handleAcceptDocument}
                >
                  {isAcceptingDocument ? (
                    <>
                      <Spinner size={20} />
                      Procesando...
                    </>
                  ) : (
                    <>
                      Acepto
                      <IonIcon style={{ marginLeft: '8px' }} icon="ios-checkmark" />
                    </>
                  )}
                </Button>
              </div>
            </PDFViewer>
          </div>
        </div>

        <style jsx>{sequentialDocuments}</style>
      </div>
    )
  }

  render() {
    const {
      isOpen,
      onClose,
      handleSubmit,
      invalid,
      loading,
      fiscalInfoFeedback,
      fiscalDocuments,
    } = this.props

    const { showPDFViewer, currentPDF, currentPDFName, isFullscreenFallback } = this.state
    const isSubmitting = fiscalInfoFeedback && fiscalInfoFeedback.loading
    const hasDocuments =
      fiscalDocuments && fiscalDocuments.length > 0

    if (showPDFViewer && currentPDF) {
      return (
        <>
          <div className={`modal-overlay ${isOpen ? 'visible' : ''}`} />
          <Modal
            isOpen={isOpen}
            onClose={this.handleClosePDFViewer}
            showCloseButton={false}
            closeOnOutClick={false}
            width="95%"
            maxWidth="1200px"
          >
            <PDFViewer
              file={currentPDF}
              documentName={currentPDFName}
              onClose={this.handleClosePDFViewer}
            />
            <style jsx>{styles}</style>
          </Modal>
        </>
      )
    }

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
     
    return (
      <div className={`fiscal-info-container ${isFullscreenFallback ? 'full-screen' : ''}`}>
        <Head>
          {/* Note: iOS+10 ignores this meta tag */}
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
          />
        </Head>

        {/* Logout button outside the modal */}
        {!hasDocuments && isOpen && !isMobile && (
          <div
            style={{
              position: 'fixed',
              top: '10px',
              right: '20px',
              zIndex: 1100,
            }}
          >
            <Button
              id="fiscal-logout-button-desktop"
              type="button"
              color="danger"
              size="small"
              onClick={() => window.location.assign('/salir')}
            >
              <IonIcon icon="ios-log-out" style={{ marginRight: '1rem' }} />
              Cerrar Sesión
            </Button>
          </div>
        )}

        <Modal
          isOpen={isOpen}
          onClose={onClose}
          showCloseButton={false}
          closeOnOutClick={false}
          className="fiscal-info-modal"
        >
          <GeneratingDocuments isSubmitting={isSubmitting}>
          <div className={classNames("fiscal-modal-container", {
            'has-documents': hasDocuments,
          })}>
            {/* Loading overlay for document generation */}

            {!hasDocuments && (
              <div className="modal-header">
                <h2>Información Fiscal Requerida</h2>
                <div className="modal-header-content">
                  <p className="modal-subtitle">
                    Complete la siguiente información para continuar usando la
                    plataforma
                  </p>
                  <FiscalInfoModalTour />
                </div>
              </div>
            )}

            {hasDocuments ? (
              this.renderSequentialDocuments()
            ) : (
              <form
                onSubmit={handleSubmit(this.handleFormSubmit)}
                className="fiscal-form"
              >
                <div className="form-content">
                  {/* RFC Field */}
                  <Row className="fiscal-form-row">
                    <Col xs={12} md={6} data-tour="fiscal-rfc">
                      <Field
                        required
                        name="rfc"
                        id="rfc"
                        component={InputForm}
                        placeholder="RFC (Registro Federal de Contribuyentes)"
                        label="RFC *"
                        className="form-input-margin"
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Field
                        required
                        name="nationality"
                        id="nationality"
                        component={SimpleDropDownForm}
                        label="Nacionalidad *"
                      >
                        {this.props.nationalitiesOptions}
                      </Field>
                    </Col>
                  </Row>

                  {/* CURP Field */}
                  <Row className="fiscal-form-row">
                    <Col xs={12} data-tour="fiscal-curp">
                      <Field
                        name="curp"
                        id="curp"
                        component={InputForm}
                        placeholder="CURP (Clave Única de Registro de Población)"
                        label={this.props.nationality === 'MX' ? 'CURP *' : 'CURP'}
                        className="form-input-margin"
                        required={this.props.nationality === 'MX'}
                      />
                      {fiscalInfoFeedback?.error &&
                        fiscalInfoFeedback?.errors?.curp && (
                          <div className="field-error">
                            {typeof fiscalInfoFeedback.errors.curp === 'string'
                              ? fiscalInfoFeedback.errors.curp
                              : fiscalInfoFeedback.errors.curp?.message ||
                                Object.values(
                                  fiscalInfoFeedback.errors.curp,
                                )[0] ||
                                'Error en CURP'}
                          </div>
                        )}
                    </Col>
                  </Row>

                  {/* Notification Address */}
                  <Row className="fiscal-form-row">
                    <Col xs={12}>
                      <Field
                        name="notificationAddress"
                        id="notification-address"
                        component={InputForm}
                        placeholder="Domicilio de notificación"
                        label="Domicilio de notificación *"
                        className="form-input-margin"
                        required
                      />
                    </Col>
                  </Row>

                  {/* Beneficiaries */}
                  <FieldArray
                    name="beneficiaries"
                    component={BeneficiaryFields}
                  />
                </div>

                <div className="modal-actions">
                  {/* Percentage indicator */}
                  <PercentageIndicator
                    beneficiaries={this.props.beneficiaries}
                  />

                  {/* General form errors */}
                  {fiscalInfoFeedback?.error && fiscalInfoFeedback?.errors && (
                    <div className="form-errors">
                      {Object.entries(fiscalInfoFeedback.errors)
                        .filter(
                          ([field, error]) => 
                            field !== 'errorResponseStatus' && field !== 'errorCodes',
                        ) // Skip status codes and error codes summary
                        .map(([field, error]) => {
                          // Field name mapping for user-friendly display
                          const fieldLabels = {
                            notificationAddress: 'Domicilio de notificación',
                            rfc: 'RFC',
                            curp: 'CURP',
                            nationality: 'Nacionalidad',
                            // Add more field mappings as needed
                          }

                          // Get user-friendly field name
                          const fieldLabel = fieldLabels[field] || field

                          // Extract the actual error message
                          let errorMessage = error
                          if (typeof error === 'object' && error !== null) {
                            // If it's an object, get the first value or the message property
                            errorMessage =
                              error.message ||
                              Object.values(error)[0] ||
                              JSON.stringify(error)
                          } else if (Array.isArray(error)) {
                            errorMessage = error.join(', ')
                          }

                          return (
                            <div key={field} className="form-error">
                              <strong>{fieldLabel}:</strong> {errorMessage}
                            </div>
                          )
                        })}
                    </div>
                  )}

                  <Row className="fiscal-form-row">
                    <Col xs={12}>
                      <Button
                        type="submit"
                        color="primary"
                        fullWidth
                        disabled={invalid || isSubmitting}
                        loading={isSubmitting}
                      >
                        {isSubmitting
                          ? 'Generando documentos...'
                          : 'Guardar información'}
                      </Button>
                    </Col>
                    {
                      isMobile && (
                        <Col xs={12} style={{ marginTop: '1rem', marginBottom: '5rem' }}>
                          <Button
                            id="fiscal-logout-button-mobile"
                            type="button"
                        color="danger"
                        fullWidth
                        onClick={() => window.location.assign('/salir')}
                      >
                        <IonIcon icon="ios-log-out" style={{ marginRight: '1rem' }} />
                        Cerrar Sesión
                      </Button>
                    </Col>
                    )}                  
                  </Row>
                </div>
              </form>
            )}
          </div>
          </GeneratingDocuments>
        </Modal>
        <style jsx>{styles}</style>
      </div>
    )
  }
}

// Get persisted form data or use defaults with broker data preloading
const getInitialFormValues = (initialBrokerData = {}) => {
  const storedFormData = getStoredFormData()
  
  // Base form values from broker data
  const brokerFormValues = {
    nationality: initialBrokerData.nationality || '',
    rfc: initialBrokerData.rfc || '',
    curp: initialBrokerData.curp || '',
    notificationAddress: initialBrokerData.notificationAddress || '',
    beneficiaries: [{}], // Start with one empty beneficiary
  }
  
  if (storedFormData) {
    const finalValues = {
      ...brokerFormValues, // Use broker data as base
      ...storedFormData, // Override with stored form data if available
      // Ensure beneficiaries array exists and has at least one item
      beneficiaries: storedFormData.beneficiaries && storedFormData.beneficiaries.length > 0 
        ? storedFormData.beneficiaries 
        : [{}]
    }
    return finalValues
  }
  
  return brokerFormValues
}

const getFormConfig = (initialBrokerData) => ({
  form: 'fiscalInfoForm',
  validate: fiscalInfoValidator,
  enableReinitialize: true,
  initialValues: getInitialFormValues(initialBrokerData),
})

const selector = formValueSelector('fiscalInfoForm')

const mapStateToProps = state => {
  const fiscalDocuments = []

  for (const documentKey of [BENEFICIARY_DECLARATION, SERVICE_CONTRACT, BUSINESS_STRATEGY_CONTRACT]) {
    if (!state.fiscalInfo.documents[documentKey]) {
      continue
    }
    
    fiscalDocuments.push({
      key: documentKey,
      url: state.fiscalInfo.documents[documentKey],
    })
  }

  const nationalities = getNationalities(state)
  const nationalitiesOptions = optionsForSelect(
    nationalities.map(nationality => ({
      value: nationality.code,
      label: nationality.name,
    }))
  )

  // Get initial broker data for form preloading
  const initialBrokerData = getInitialInfoBrokerDataSelector(state)

  return {
    beneficiaries: selector(state, 'beneficiaries'),
    nationality: selector(state, 'nationality'),
    fiscalInfoFeedback: state.fiscalInfo.feedback,
    fiscalDocuments,
    acceptanceFeedback: state.fiscalInfo.acceptanceFeedback,
    nationalitiesOptions,
    // Add progress tracking from state
    acceptedDocuments: state.fiscalInfo.acceptedDocuments || [],
    currentDocumentIndex: state.fiscalInfo.currentDocumentIndex || 0,
    // Add initial broker data for preloading
    initialBrokerData,
  }
}

const mapDispatchToProps = {
  generateInitialDocuments: FiscalInfoActionCreators.generateInitialDocuments,
  acceptContracts: FiscalInfoActionCreators.acceptContracts,
  acceptDocument: FiscalInfoActionCreators.acceptDocument,
  updateDocumentProgress: FiscalInfoActionCreators.updateDocumentProgress,
  getInitialInfoBroker: store.getInitialInfoBroker('user').action,
}

const formConfig = {
  form: 'fiscalInfoForm',
  validate: fiscalInfoValidator,
  enableReinitialize: false,
  touchOnBlur: true,
  touchOnChange: true,
  initialValues: {
    nationality: '',
    rfc: '',
    curp: '',
    notificationAddress: '',
    beneficiaries: [{}],
  },
}

const ConnectedFiscalInfoModal = connect(
  mapStateToProps,
  mapDispatchToProps,
)(reduxForm(formConfig)(FiscalInfoModal))

export default ConnectedFiscalInfoModal
